// Same localStorage key and value shape the zion flag overlay (?showFlags) writes;
// @fs/flags-js checks these overrides before consulting Split.
const STORAGE_KEY = 'fsZionFlags'

// Umbrella flag the feedback group's standing opt-in link enables
// (?flags=standards_places_preRelease:on — see docs/feature-flags.md).
export const PRE_RELEASE_FLAG = 'standards_places_preRelease'

type FlagOverrides = Record<string, { treatment: string; config: null }>

function readOverrides(): FlagOverrides {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function hasFlagOverrides(): boolean {
  return Object.keys(readOverrides()).length > 0
}

export function clearFlagOverrides(): void {
  window.localStorage.removeItem(STORAGE_KEY)
}

// Applies the feedback group's one-click opt-in links:
//   ?flags=standards_places_preRelease:on   ?flags=clear
// Only the pre-release flag can be set from a URL — it is kill-switched per
// environment in Split (see usePreReleaseFlag), unlike arbitrary flags, which
// would win their override unconditionally and stay overlay-only on purpose.
// Must run before React renders so FeatureFlagsProvider picks the override up
// on this page load (no reload needed). Strips the param afterward so copied
// URLs don't re-apply it.
export default function applyFlagLinkOverrides(): void {
  const params = new URLSearchParams(window.location.search)
  const flagsParam = params.get('flags')
  if (!flagsParam) return

  if (flagsParam === 'clear') {
    clearFlagOverrides()
  } else {
    const [name, treatment] = flagsParam.split(':')
    if (name !== PRE_RELEASE_FLAG || (treatment !== 'on' && treatment !== 'off')) return
    const overrides = readOverrides()
    overrides[name] = { treatment, config: null }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides))
  }

  params.delete('flags')
  const query = params.toString()
  const search = query ? `?${query}` : ''
  window.history.replaceState(null, '', `${window.location.pathname}${search}${window.location.hash}`)
}
